import { APIEndpoints } from '@app/enums/APIEndpoints';
import type { FileModel } from '@app/models/api/FileModel';
import type { OperationSummaryModel } from '@app/models/api/OperationSummaryModel';
import { translateOperationSummaryResponseArray } from '@app/models/api/OperationSummaryModel';
import type { PaginationModel } from '@app/models/api/PaginationModel';
import type { OperationModel } from '@app/models/api/OperationModel';
import { translateOperationResponse } from '@app/models/api/OperationModel';
import type { BondsSimulator } from '@app/models/api/BondsSimulator';
import type { AlertsModelOperation } from '@app/models/api/AlertsModelOperation';
import type { DirsModel } from '@app/models/api/DirsModel';
import { translateDirsModel } from '@app/models/api/DirsModel';
import { downloadFile } from '../files';
import { snackbar } from '$lib/stores';
import { t } from 'svelte-i18n';
import { get } from 'svelte/store';
import type { Fetch } from '@app/types/Global';
import { error } from '@sveltejs/kit';
import type { OperationRegulationsEnum } from '@app/models/api/enums/OperationStatusEnum';
import { getCardComponent } from '@app/utils/operations';

export const getOperations = async (
	query?: string,
	apiUrl?: string | null,
	sveltekitFetch?: Fetch
): Promise<PaginationModel<OperationSummaryModel[]>> => {
	const queryParams = query ?? '';

	const url = apiUrl
		? `${apiUrl}${APIEndpoints.OPERATIONS()}?${queryParams}`
		: `${APIEndpoints.OPERATIONS()}?${queryParams}`;

	const res = sveltekitFetch ? await sveltekitFetch(url) : await fetch(url);

	const json = await res.json();
	if (res.ok) {
		if (res.status === 204) {
			return { data: [], page: {} };
		} else {
			const data = translateOperationSummaryResponseArray(json?._embedded?.operations);
			const page = json?.page;
			return { data, page };
		}
	} else {
		error(res.status);
	}
};
export const loadOperationsWithOptions = async (
	url: URL,
	regulation: OperationRegulationsEnum,
	instruments: string | null = null,
	fetch?: Fetch
): Promise<PaginationModel<OperationSummaryModel[]>> => {
	const status = url.searchParams.get('statuts');
	const avantages = url.searchParams.get('avantages');
	const search = url.searchParams.get('search');
	const categories = url.searchParams.get('categories');
	const page = url.searchParams.get('page');

	const queryParams = new URLSearchParams({
		reglementations: regulation,
		...(instruments
			? { instruments: instruments }
			: url.searchParams.has('instruments')
				? { instruments: url.searchParams.get('instruments') ?? '' }
				: {}),
		...(status && { statuts: status }),
		...(avantages && { avantages }),
		...(search && { search }),
		...(categories && { categories }),
		...(page && { page })
	});
	const operations = await getOperations(queryParams.toString(), null, fetch);
	operations.data = await Promise.all(
		operations.data.map(async (o): Promise<OperationSummaryModel> => {
			try {
				o.svelteComp = await getCardComponent(o);
			} catch (e) {
				console.log(e);
			}
			return o;
		})
	);
	return operations;
};

export const getOperation = async (
	id: string,
	apiUrl?: string | null,
	sveltekitFetch?: Fetch
): Promise<OperationModel> => {
	const url = apiUrl ? `${apiUrl}${APIEndpoints.OPERATIONS(id)}` : `${APIEndpoints.OPERATIONS(id)}`;
	const res = sveltekitFetch ? await sveltekitFetch(url) : await fetch(url);
	const json = await res.json();

	if (res.ok) {
		return translateOperationResponse(json);
	} else {
		throw json;
	}
};

export const followOperation = async (id: string) => {
	const res = await fetch(APIEndpoints.OPERATIONS(id, 'followers'), { method: 'PUT' });
	const json = await res.json();
	if (res.ok) {
		return;
	} else {
		throw json;
	}
};

export const unfollowOperation = async (id: string) => {
	const res = await fetch(APIEndpoints.OPERATIONS(id, 'followers'), { method: 'DELETE' });
	const json = await res.json();
	if (res.ok) {
		return;
	} else {
		throw json;
	}
};

export const voteForOperation = async (id: string, data: Record<string, any>) => {
	const res = await fetch(APIEndpoints.OPERATIONS(id, 'votes'), {
		method: 'PUT',
		body: JSON.stringify(data)
	});
	const json = await res.json();
	if (res.ok) {
		return;
	} else {
		throw json;
	}
};

export const getOperationDirs = async (operationId: string): Promise<DirsModel> => {
	const res = await fetch(APIEndpoints.OPERATIONS(operationId, 'documents/dirs'));
	const json = await res.json();
	if (res.ok) {
		return translateDirsModel(json);
	} else {
		throw json;
	}
};

export const getOperationSubscriptionConditions = async (operationId: string): Promise<string> => {
	const res = await fetch(APIEndpoints.OPERATIONS(operationId, 'conditions'));
	const json = await res.json();
	if (res.ok) {
		return json?.contenu;
	} else {
		throw json;
	}
};

export const getOperationSimulation = async (
	operationId: string,
	amount: number,
	baseUrl: string = ''
): Promise<BondsSimulator> => {
	// if the operation type == "INTERETS_MENSUELS_SUR_CAPITAL_AMORTI" API return 404
	const res = await fetch(
		`${baseUrl}${APIEndpoints.OPERATIONS(operationId, 'simulation')}?amount=${amount}`
	);
	const json = await res.json();
	if (res.ok) {
		return json;
	} else {
		throw json;
	}
};

export const alertSms = async (id: string, data: AlertsModelOperation) => {
	const res = await fetch(APIEndpoints.OPERATIONS(id, 'alerts'), {
		method: 'POST',
		body: JSON.stringify(data)
	});
	const json = await res.json();
	if (res.ok) {
		return json;
	} else {
		throw json;
	}
};

export const getAllDocuments = async (operationId: string) => {
	const fm: FileModel = {
		creationDate: '',
		fileName: `PACK_DOCUMENTS_${operationId}.zip`,
		fileType: 'zip',
		id: 0,
		mimeType: 'application/zip',
		title: `PACK_DOCUMENTS_${operationId}.zip`,
		url: APIEndpoints.OPERATIONS(operationId, 'documents')
	};
	try {
		const $t = get(t);
		snackbar.open({ message: $t('taxation.loading') });
		await downloadFile(fm);
	} catch (e) {
		snackbar.danger(e.message);
		throw e;
	}
};

export const postOperationCode = async (
	code: string,
	recaptchaToken: string | undefined
): Promise<{ accesOperationId: number; url: string }> => {
	const res = await fetch(APIEndpoints.OPERATION_PRIVATE_CODE, {
		method: 'POST',
		body: JSON.stringify({
			value: code,
			'g-recaptcha-response': recaptchaToken
		})
	});
	const json = await res.json();

	if (res.ok) {
		return json;
	} else {
		throw json;
	}
};
